import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Home.css";

const BASE_URL = "https://ikaze-thouse-backend.vercel.app";
const API_SLIDES = `${BASE_URL}/api/slides/all`;
const API_RENTALS = `${BASE_URL}/api/items`;
const API_RENTAL_BOOKINGS = `${BASE_URL}/api/v1/bookings`;
const API_SALES = `${BASE_URL}/api/houses`;
const API_SALE_BOOKINGS = `${BASE_URL}/api/Salebooking`;

// --- Reusable Read More / Read Less Component ---
function ReadMoreText({ text, maxLength = 85 }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;
  
  if (text.length <= maxLength) {
    return <p className="property-card-excerpt-full">{text}</p>;
  }

  return (
    <p className="property-card-excerpt">
      {isExpanded ? text : `${text.substring(0, maxLength)}...`}
      <button 
        type="button" 
        className="read-more-toggle-link"
        onClick={(e) => {
          e.stopPropagation(); // Prevents click from bubbling up to parent handlers
          setIsExpanded(!isExpanded);
        }}
      >
        {isExpanded ? "Read Less" : "Read More"}
      </button>
    </p>
  );
}

export default function Home() {
  const navigate = useNavigate();

  // --- UI States ---
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'sale' | 'rent'
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Data Storage ---
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [rentals, setRentals] = useState([]);
  const [sales, setSales] = useState([]);

  // --- Availability Status ---
  const [reservedRentalUnits, setReservedRentalUnits] = useState([]); 
  const [soldOutSaleIds, setSoldOutSaleIds] = useState([]); 

  // --- Modal Form State ---
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [saleForm, setSaleForm] = useState({
    house_id: "", house_title: "", price_usd: "", booking_amount: "",
    location: "", bedrooms: "", bathrooms: "", customer_name: "",
    email: "", phone: "", national_id: "", address: "", payment_date: "", notes: ""
  });

  // --- Helper Functions ---
  const cleanUnitString = (str) => {
    if (!str) return "";
    return str.toString().toLowerCase().replace(/[\s\-_]/g, "").trim();
  };

  const getImgUrl = (url) => {
    if (!url) return "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  // --- Image Slider Controls ---
  const nextSlide = useCallback(() => {
    setCurrentSlideIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlideIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  useEffect(() => {
    if (slides.length > 0) {
      const interval = setInterval(nextSlide, 6000);
      return () => clearInterval(interval);
    }
  }, [nextSlide, slides.length]);

  // --- Fetch Data from APIs ---
  useEffect(() => {
    const fetchPlatformData = async () => {
      try {
        setLoading(true);

        const [resSlides, resRentals, resRentalBookings, resSales, resSaleBookings] = await Promise.all([
          axios.get(API_SLIDES),
          axios.get(API_RENTALS),
          axios.get(API_RENTAL_BOOKINGS).catch(() => ({ data: [] })),
          axios.get(API_SALES),
          axios.get(API_SALE_BOOKINGS).catch(() => ({ data: [] }))
        ]);

        setSlides(resSlides.data || []);
        setRentals(resRentals.data || []);
        setSales(resSales.data || []);

        // Find items that are already rented out
        const rawRentalBookings = Array.isArray(resRentalBookings.data)
          ? resRentalBookings.data
          : (resRentalBookings.data?.bookings || []);

        const activeRentals = [];
        rawRentalBookings.forEach((b) => {
          if (!b) return;
          const status = (b.status || "").toString().toLowerCase().trim();
          const unitField = b.appartment_unit || b.apartment_unit || "";
          if (status !== "" && status !== "rejected" && status !== "reject") {
            if (unitField) activeRentals.push(cleanUnitString(unitField));
          }
        });
        setReservedRentalUnits(activeRentals);

        // Find houses that are already sold
        if (Array.isArray(resSaleBookings.data)) {
          const activeSales = resSaleBookings.data
            .filter((b) => b.status !== "Reject" && b.status !== "Rejected" && b.status !== "reject")
            .map((b) => String(b.house_id));
          setSoldOutSaleIds(activeSales);
        }

      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformData();
  }, [nextSlide]);

  // --- Action Handlers ---
  const handleRentNavigation = (item) => {
    const isReserved = reservedRentalUnits.includes(cleanUnitString(item.unitname));
    if (isReserved) return;
    navigate("/Booking", {
      state: { unitname: item.unitname, price: item.price }
    });
  };

  const openSaleBookingModal = (house) => {
    if (soldOutSaleIds.includes(String(house.id))) return;
    const standardDeposit = Number(house.price_usd || 0) * 0.25;

    setSaleForm({
      house_id: house.id,
      house_title: house.title,
      price_usd: house.price_usd,
      booking_amount: standardDeposit,
      location: house.location || "Kigali, Rwanda",
      bedrooms: house.bedrooms || "--",
      bathrooms: house.bathrooms || "--",
      customer_name: "",
      email: "",
      phone: "",
      national_id: "",
      address: "",
      payment_date: "",
      notes: ""
    });
    setShowSaleModal(true);
  };

  const submitSaleForm = async (e) => {
    e.preventDefault();
    if (!saleForm.customer_name || !saleForm.email || !saleForm.phone) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(API_SALE_BOOKINGS, saleForm);
      alert("Your purchase request has been sent successfully!");
      setSoldOutSaleIds((prev) => [...prev, String(saleForm.house_id)]);
      setShowSaleModal(false);
    } catch (err) {
      console.error(err);
      alert("Error processing your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="platform-loading-viewport">
        <div className="lux-spinner-wrapper">
          <div className="lux-spinner-ring"></div>
          <span className="lux-spinner-brand">GKM INVEST</span>
        </div>
        <p className="lux-loading-text">Loading properties...</p>
      </div>
    );
  }

  return (
    <div className="home-page-container">
      
      {/* FIXED CSS OVERRIDES: Removes image compression and drops dark mask completely */}
      <style>{`
        .hero-slider-section {
          background-color: #0f172a; 
        }

        .hero-slide-background {
          background-size: cover !important; 
          background-repeat: no-repeat !important;
          background-position: center center !important;
        }

        /* Dark mask overlay fully cleared */
        .hero-slide-mask {
          background: none !important;
        }

        /* Drop shadows around typography keeps text legible over unmasked high-brightness photos */
        .hero-slide-title {
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.85), 0 1px 3px rgba(0, 0, 0, 0.9) !important;
        }
        .hero-slide-description {
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85) !important;
          font-weight: 500 !important;
        }
        .hero-slide-tagline {
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6) !important;
        }
      `}</style>
      
      {/* 1. HERO SLIDER */}
      {slides.length > 0 && (
        <section className="hero-slider-section">
          {slides.map((slide, index) => (
            <div key={slide.id || index} className={`hero-slide-item ${index === currentSlideIndex ? "slide-state-active" : ""}`}>
              <div className="hero-slide-background" style={{ backgroundImage: `url(${getImgUrl(slide.image_url)})` }} />
              <div className="hero-slide-mask" />
              <div className="hero-slide-content-box">
                <span className="hero-slide-tagline">Beautiful Homes & Real Estate Portfolios</span>
                <h1 className="hero-slide-title">{slide.title}</h1>
                <p className="hero-slide-description">{slide.description}</p>
                <a href="#catalog-anchor" className="hero-slide-gold-btn">View Live Properties</a>
              </div>
            </div>
          ))}

          <div className="carousel-control-overlay">
            <button onClick={prevSlide} className="carousel-arrow-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div className="carousel-indicator-dots">
              {slides.map((_, idx) => (
                <span key={idx} className={`carousel-dot ${idx === currentSlideIndex ? "dot-state-active" : ""}`} onClick={() => setCurrentSlideIndex(idx)} />
              ))}
            </div>
            <button onClick={nextSlide} className="carousel-arrow-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </section>
      )}

      {/* 2. VALUE PROPOSITION STRIP */}
      <section className="value-proposition-strip">
        <div className="value-strip-container">
          <div className="value-proposition-card">
            <div className="value-icon-box">✓</div>
            <div className="value-text-box">
              <h4>Verified Titles</h4>
              <p>Every single property has a checked and verified clean legal title deed.</p>
            </div>
          </div>
          <div className="value-proposition-card">
            <div className="value-icon-box">★</div>
            <div className="value-text-box">
              <h4>Best Locations</h4>
              <p>All properties are placed in fast-growing, high-value urban areas.</p>
            </div>
          </div>
          <div className="value-proposition-card">
            <div className="value-icon-box">🛡</div>
            <div className="value-text-box">
              <h4>Secure Booking</h4>
              <p>Your downpayments and bookings are protected through safe escrow methods.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FILTERABLE PROPERTY CATALOG */}
      <main id="catalog-anchor" className="catalog-display-section">
        <div className="catalog-header-block">
          <span className="catalog-pre-title">OUR PROPERTY CATALOG</span>
          <h2>Live Property List</h2>
          <p className="catalog-subtitle-text">Find verified properties available right now for lease or direct purchase.</p>
          
          <div className="catalog-tab-navigator">
            <button onClick={() => setActiveTab("all")} className={`catalog-tab-pill ${activeTab === "all" ? "pill-state-active" : ""}`}>
              All Properties <span className="pill-count-badge">{rentals.length + sales.length}</span>
            </button>
            <button onClick={() => setActiveTab("sale")} className={`catalog-tab-pill ${activeTab === "sale" ? "pill-state-active" : ""}`}>
              For Sale <span className="pill-count-badge">{sales.length}</span>
            </button>
            <button onClick={() => setActiveTab("rent")} className={`catalog-tab-pill ${activeTab === "rent" ? "pill-state-active" : ""}`}>
              For Rent <span className="pill-count-badge">{rentals.length}</span>
            </button>
          </div>
        </div>

        <div className="property-cards-grid">
          
          {/* --- FOR LEASE / RENTAL LOOP --- */}
          {(activeTab === "all" || activeTab === "rent") &&
            rentals.map((item) => {
              const isReserved = reservedRentalUnits.includes(cleanUnitString(item.unitname));
              return (
                <div key={`rental-card-${item.id}`} className={`property-showcase-card ${isReserved ? "card-modifier-dimmed" : ""}`}>
                  <div className="property-card-media-frame">
                    <img className="property-card-image" src={getImgUrl(item.image_url)} alt={item.description} loading="lazy" />
                    <span className={`property-status-badge ${isReserved ? "badge-state-negative" : "badge-state-lease"}`}>
                      {isReserved ? "RENTED OUT" : "AVAILABLE TO RENT"}
                    </span>
                    <div className="property-card-location-meta">Unit: {item.unitname}</div>
                  </div>
                  <div className="property-card-details-frame">
                    <span className="property-structural-tag">Apartment Details</span>
                    <h3 className="property-main-title">Luxury Rental Unit</h3>
                    
                    <div className="property-card-metrics-row">
                      <span><strong>Cleaner</strong> </span>
                      <span className="metric-dot-divider">•</span>
                      <span><strong>Electricity</strong> </span>
                      <span className="metric-dot-divider">•</span>
                      <span><strong>Washing Machine</strong> </span>
                      <span className="metric-dot-divider">•</span>
                      <span><strong>Water</strong> </span>
                      <span className="metric-dot-divider">•</span>
                      <span>WiFi</span>
                    </div>

                    <ReadMoreText text={item.description} maxLength={85} />

                    <div className="property-price-tag">
                      {item.price} <span className="price-frequency">USD / Month</span>
                    </div>
                    <button
                      className={`property-card-action-btn ${isReserved ? "action-btn-disabled" : "action-btn-lease"}`}
                      disabled={isReserved}
                      onClick={() => handleRentNavigation(item)}
                    >
                      {isReserved ? "Occupied" : "Book This Rental"}
                    </button>
                  </div>
                </div>
              );
            })}

          {/* --- FOR SALE / PURCHASE LOOP --- */}
          {(activeTab === "all" || activeTab === "sale") &&
            sales.map((house) => {
              const isSoldOut = soldOutSaleIds.includes(String(house.id));
              return (
                <article key={`sale-card-${house.id}`} className={`property-showcase-card ${isSoldOut ? "card-modifier-dimmed" : ""}`}>
                  <div className="property-card-media-frame">
                    <img className="property-card-image" src={getImgUrl(house.image_url)} alt={house.title} loading="lazy" />
                    <span className={`property-status-badge ${isSoldOut ? "badge-state-negative" : "badge-state-purchase"}`}>
                      {isSoldOut ? "SOLD" : "FOR SALE"}
                    </span>
                    <div className="property-card-location-meta">{house.location || "Kigali, Rwanda"}</div>
                  </div>
                  <div className="property-card-details-frame">
                    <span className="property-structural-tag">House / Estate</span>
                    <h3 className="property-main-title">{house.title}</h3>
                    <div className="property-card-metrics-row">
                      <span><strong>{house.bedrooms || "--"}</strong> Beds</span>
                      <span className="metric-dot-divider">•</span>
                      <span><strong>{house.bathrooms || "--"}</strong> Baths</span>
                      <span className="metric-dot-divider">•</span>
                      <span>Deed Verified</span>
                    </div>

                    <ReadMoreText text={house.description} maxLength={85} />

                    <div className="property-price-tag">
                      {Number(house.price_usd || 0).toLocaleString()} <span className="price-frequency">RWF</span>
                    </div>
                    <button
                      className={`property-card-action-btn ${isSoldOut ? "action-btn-disabled" : "action-btn-purchase"}`}
                      disabled={isSoldOut}
                      onClick={() => openSaleBookingModal(house)}
                    >
                      {isSoldOut ? "Sold Out" : "Buy This Property"}
                    </button>
                  </div>
                </article>
              );
            })}
        </div>
      </main>

      {/* 4. PURCHASE BOOKING MODAL */}
      {showSaleModal && (
        <div className="modal-backdrop-blur" onClick={() => !isSubmitting && setShowSaleModal(false)}>
          <div className="modal-architecture-card" onClick={(e) => e.stopPropagation()}>
            
            <div className="modal-sidebar-branding">
              <div className="modal-sidebar-logo">GKM <span>INVEST</span></div>
              <h3>Property Purchase</h3>
              <p>Fill out this booking form to officially hold the property under your name while legal steps are prepared.</p>
              <div className="modal-compliance-bullets">
                <div className="compliance-item">✓ Direct Title Deed Transfer</div>
                <div className="compliance-item">✓ Checked Escrow Secure Steps</div>
                <div className="compliance-item">✓ Full Lawyer Guide Included</div>
              </div>
            </div>

            <div className="modal-content-workspace">
              <div className="modal-workspace-header">
                <h2>House Booking Form</h2>
                <button className="modal-close-x-btn" onClick={() => !isSubmitting && setShowSaleModal(false)} disabled={isSubmitting}>&times;</button>
              </div>

              <form onSubmit={submitSaleForm} className="modal-interactive-form">
                <div className="modal-invoice-preview">
                  <div className="invoice-meta-line">
                    <span>Chosen Property:</span>
                    <strong>{saleForm.house_title}</strong>
                  </div>
                  <div className="invoice-meta-line">
                    <span>Full Property Price:</span>
                    <span className="invoice-price-text">{Number(saleForm.price_usd || 0).toLocaleString()} RWF</span>
                  </div>
                  <div className="invoice-meta-line structural-highlight-line">
                    <span>Required Hold Deposit (25%):</span>
                    <strong>{Number(saleForm.booking_amount || 0).toLocaleString()} RWF</strong>
                  </div>
                </div>

                <div className="modal-fields-grid-layout">
                  <div className="form-field-wrapper">
                    <label>Your Full Legal Name *</label>
                    <input type="text" placeholder="e.g. Danny Niyitanga" required value={saleForm.customer_name} onChange={(e) => setSaleForm({ ...saleForm, customer_name: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper">
                    <label>Email Address *</label>
                    <input type="email" placeholder="name@domain.com" required value={saleForm.email} onChange={(e) => setSaleForm({ ...saleForm, email: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper">
                    <label>Phone Number *</label>
                    <input type="tel" placeholder="+250 788 000 000" required value={saleForm.phone} onChange={(e) => setSaleForm({ ...saleForm, phone: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper">
                    <label>National ID / Passport Number</label>
                    <input type="text" placeholder="1 1999 8 0000000 0 00" value={saleForm.national_id} onChange={(e) => setSaleForm({ ...saleForm, national_id: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper field-modifier-fullwidth">
                    <label>Current Home Address</label>
                    <input type="text" placeholder="Kigali, Rwanda" value={saleForm.address} onChange={(e) => setSaleForm({ ...saleForm, address: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper">
                    <label>Expected Payment Date</label>
                    <input type="date" value={saleForm.payment_date} onChange={(e) => setSaleForm({ ...saleForm, payment_date: e.target.value })} />
                  </div>
                  <div className="form-field-wrapper field-modifier-fullwidth">
                    <label>Extra Notes or Requests</label>
                    <textarea rows="2" placeholder="Write any extra details or custom requests here..." value={saleForm.notes} onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })} />
                  </div>
                </div>

                <div className="modal-action-row-footer">
                  <button type="button" className="modal-cancel-btn" onClick={() => setShowSaleModal(false)} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="modal-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? "Sending Request..." : "Submit Purchase Booking"}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}